import type { CCCard } from "./types";

export type HistoryEntry = {
  wallet: string;
  dumpedAt: string;
  totalCards: number;
  // Cards may be absent when localStorage was full when this entry was
  // saved. The history item still shows up so the user can re-fetch.
  cards?: CCCard[];
};

export type SaveOutcome = {
  entries: HistoryEntry[];
  // Number of *previous* entries whose cached cards were dropped to fit
  // this save. Their metadata is preserved.
  evictedFromOthers: number;
  // True when the new entry itself couldn't be cached (metadata only).
  // Implies older entries were trimmed first.
  newEntryEvicted: boolean;
};

const STORAGE_KEY = "cc-wallet-dumper:history:v1";
const MAX_ENTRIES = 20;

const HAS_COMPRESSION =
  typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";

// Versioned envelope for compressed storage. Legacy data (a bare JSON
// array) loads transparently and gets migrated on the next save.
type StoredEnvelope = { v: 1; c: "gzip"; d: string; raw: number };

function isEnvelope(x: unknown): x is StoredEnvelope {
  return (
    !!x &&
    typeof x === "object" &&
    (x as StoredEnvelope).v === 1 &&
    (x as StoredEnvelope).c === "gzip" &&
    typeof (x as StoredEnvelope).d === "string" &&
    typeof (x as StoredEnvelope).raw === "number"
  );
}

async function compressToBase64(text: string): Promise<string> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream("gzip"));
  const buf = await new Response(stream).arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

async function decompressFromBase64(b64: string): Promise<string> {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Response(stream).text();
}

function isValidEntry(e: unknown): e is HistoryEntry {
  if (!e || typeof e !== "object") return false;
  const o = e as HistoryEntry;
  return (
    typeof o.wallet === "string" &&
    typeof o.dumpedAt === "string" &&
    typeof o.totalCards === "number" &&
    (o.cards === undefined || Array.isArray(o.cards))
  );
}

// Synchronous load — only succeeds for legacy (uncompressed) data.
// Compressed entries return [] here; the App calls loadHistoryAsync()
// from useEffect to populate them.
export function loadHistorySync(): HistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(isValidEntry);
    return []; // envelope — must be loaded async
  } catch {
    return [];
  }
}

// Backwards-compatible name (returns sync result; full load needs Async).
export const loadHistory = loadHistorySync;

export async function loadHistoryAsync(): Promise<HistoryEntry[]> {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(isValidEntry);
    if (isEnvelope(parsed)) {
      const json = await decompressFromBase64(parsed.d);
      const arr = JSON.parse(json);
      return Array.isArray(arr) ? arr.filter(isValidEntry) : [];
    }
    return [];
  } catch {
    return [];
  }
}

async function trySave(entries: HistoryEntry[]): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const json = JSON.stringify(entries);
    const value = HAS_COMPRESSION
      ? JSON.stringify({
          v: 1,
          c: "gzip",
          d: await compressToBase64(json),
          raw: json.length * 2,
        } satisfies StoredEnvelope)
      : json;
    window.localStorage.setItem(STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
}

// Saves the entry with quota-aware fallback. Reports exactly what had
// to be evicted so the UI can surface it to the user.
export async function addToHistory(entry: HistoryEntry): Promise<SaveOutcome> {
  if (typeof window === "undefined") {
    return { entries: [], evictedFromOthers: 0, newEntryEvicted: false };
  }

  const baseline = await loadHistoryAsync();
  const existing = baseline.filter((e) => e.wallet !== entry.wallet);
  let next = [entry, ...existing].slice(0, MAX_ENTRIES);

  if (await trySave(next)) {
    return { entries: next, evictedFromOthers: 0, newEntryEvicted: false };
  }

  // Drop cached cards from oldest entries first (skip index 0 — the
  // new entry — until everything else has been trimmed).
  let evicted = 0;
  for (let i = next.length - 1; i > 0; i--) {
    if (!next[i]?.cards) continue;
    next = next.map((e, idx) => (idx === i ? { ...e, cards: undefined } : e));
    evicted++;
    if (await trySave(next)) {
      return { entries: next, evictedFromOthers: evicted, newEntryEvicted: false };
    }
  }

  // Last resort — strip the new entry's cards too.
  next = next.map((e) => ({ ...e, cards: undefined }));
  await trySave(next);
  return { entries: next, evictedFromOthers: evicted, newEntryEvicted: true };
}

export async function removeFromHistory(wallet: string): Promise<HistoryEntry[]> {
  if (typeof window === "undefined") return [];
  const next = (await loadHistoryAsync()).filter((e) => e.wallet !== wallet);
  await trySave(next);
  return next;
}

export async function clearHistory(): Promise<HistoryEntry[]> {
  if (typeof window === "undefined") return [];
  await trySave([]);
  return [];
}

// Drops the cached `cards` from the N oldest entries (keeps metadata).
// Useful for "free up space" actions that don't require deleting
// entries outright.
export async function trimOldestCards(n: number): Promise<HistoryEntry[]> {
  if (typeof window === "undefined") return [];
  const all = await loadHistoryAsync();
  let dropped = 0;
  const next = all.slice();
  for (let i = next.length - 1; i >= 0 && dropped < n; i--) {
    const e = next[i];
    if (!e?.cards) continue;
    next[i] = { ...e, cards: undefined };
    dropped++;
  }
  await trySave(next);
  return next;
}

export type StorageMeta = {
  onDisk: number;
  raw: number;
  compressed: boolean;
};

// Sync read of metadata only — pulls compressed/raw sizes out of the
// envelope without decompressing the payload.
export function getStorageMeta(): StorageMeta {
  if (typeof window === "undefined") {
    return { onDisk: 0, raw: 0, compressed: false };
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return { onDisk: 0, raw: 0, compressed: false };
  const onDisk = raw.length * 2;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { onDisk, raw: onDisk, compressed: false };
    if (isEnvelope(parsed)) return { onDisk, raw: parsed.raw, compressed: true };
  } catch {
    // fall through
  }
  return { onDisk, raw: onDisk, compressed: false };
}

export function getStorageBytes(): number {
  return getStorageMeta().onDisk;
}

// Soft target — most browsers cap localStorage around 5 MB per origin.
// Used purely for percentage display in the UI, not enforcement.
export const STORAGE_SOFT_CAP_BYTES = 5 * 1024 * 1024;

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatHistoryDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffMin = Math.round(diffMs / 60_000);
    const diffHour = Math.round(diffMs / 3_600_000);
    const diffDay = Math.round(diffMs / 86_400_000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export function truncateWallet(wallet: string, head = 6, tail = 4): string {
  if (wallet.length <= head + tail + 1) return wallet;
  return `${wallet.slice(0, head)}…${wallet.slice(-tail)}`;
}
